import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import WorkbookVideoPlayer from './WorkbookVideoPlayer';
import { checkAndUnlockPostAssessment, WorkbookCompletionModal } from '../utils/checkAndUnlockPostAssessment';

const AI_SCORING_PROMPT = `You are an expert AI literacy coach evaluating a learner's advanced prompting work.

Evaluate the submission below. Score each criterion 1-4 (1=Missing, 2=Partial, 3=Solid, 4=Excellent).

FOR A CHAIN PROMPT, score on:

BUILDS ON OUTPUT: Does it directly use the first output without re-explaining context? (1-4)

BREVITY: Is it noticeably shorter than the original RICE prompt? (1-4)

RESULT VALUE: Does it produce something you couldn't get from the first prompt alone? (1-4)

OVERALL: How effectively does it extend the first output? (1-4)

FOR AN ITERATION PROMPT, score on:

CORRECT MOVE: Did they choose the right move: Redirect, Sharpen, or Add Constraint? (1-4)

ONE FIX ONLY: Does it address exactly one specific issue? (1-4)

IMPROVEMENT: Did the output improve on the specific issue without breaking what was working? (1-4)

OVERALL: How effectively did it refine the response? (1-4)

After scoring:
1. Give a total score out of 16.
2. Name the single most important thing that would make this stronger.
3. Rewrite it incorporating that improvement.
4. Explain in one sentence what changed and why it matters.

Here's what I'm submitting for evaluation (specify chain or iteration):
[PASTE ORIGINAL RICE PROMPT + FIRST RESPONSE + YOUR CHAIN OR ITERATION PROMPT]`;

const CHAIN_RUBRIC = [
  { criterion: 'BUILDS ON OUTPUT', missing: 'No connection to first output', partial: 'Loosely connected', solid: 'Directly uses the first output', excellent: 'Explicitly references specific elements from the first output' },
  { criterion: 'BREVITY', missing: 'Longer than the RICE prompt', partial: 'Same length as RICE', solid: 'Noticeably shorter', excellent: 'Short and precise, no re-explaining existing context' },
  { criterion: 'RESULT VALUE', missing: 'Output less useful than first', partial: 'Marginally more useful', solid: 'Meaningfully extends the first output', excellent: "Produces something you couldn't have gotten from the first prompt alone" },
  { criterion: 'OVERALL', missing: 'No real extension', partial: 'Slight extension', solid: 'Clear, useful next step', excellent: 'Transforms first output into something significantly more valuable' },
];

const ITERATION_RUBRIC = [
  { criterion: 'CORRECT MOVE', missing: 'Wrong move for the problem', partial: 'Partially correct move', solid: 'Correct move identified and applied', excellent: 'Correct move with precise language matching the specific issue' },
  { criterion: 'ONE FIX ONLY', missing: 'Multiple changes in one prompt', partial: 'Two changes attempted', solid: 'One change, slightly vague', excellent: 'Exactly one specific, actionable fix' },
  { criterion: 'IMPROVEMENT', missing: 'Output got worse or unchanged', partial: 'Marginally better', solid: 'Clearly improved on the specific issue', excellent: 'Resolved the issue completely without breaking what was working' },
  { criterion: 'OVERALL', missing: 'No useful refinement', partial: 'Small improvement', solid: 'Meaningful refinement of the right thing', excellent: 'Fixed the issue, preserved everything that was working' },
];

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, minHeight = 60 }) {
  return (
    <div style={s.field}>
      {label && <div style={s.fieldLabel}>{label}</div>}
      <textarea style={{ ...s.textarea, minHeight }} value={value || ''} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

function ScoreField({ label, value, onChange }) {
  return (
    <div style={s.scoreField}>
      <div style={s.scoreLabel}>{label}</div>
      <select style={s.scoreSelect} value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">—</option>
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
        <option value="4">4</option>
      </select>
      <div style={s.scoreOf}>/ 4</div>
    </div>
  );
}

function RubricTable({ rows }) {
  return (
    <div style={s.rubricTable}>
      <div style={s.rubricHeader}>
        {['CRITERION', '1 — MISSING', '2 — PARTIAL', '3 — SOLID', '4 — EXCELLENT'].map(h => (
          <div key={h} style={s.rubricCol0}>{h}</div>
        ))}
      </div>
      {rows.map((row, i) => (
        <div key={i} style={s.rubricRow}>
          <div style={{ ...s.rubricCol0, background: '#F2F0EC', color: '#1B2A4A', fontWeight: '700' }}>{row.criterion}</div>
          <div style={s.rubricCol}>{row.missing}</div>
          <div style={s.rubricCol}>{row.partial}</div>
          <div style={s.rubricCol}>{row.solid}</div>
          <div style={s.rubricCol}>{row.excellent}</div>
        </div>
      ))}
    </div>
  );
}

function CalloutBox({ title, children }) {
  return (
    <div style={s.callout}>
      <div style={s.calloutTitle}>{title}</div>
      <div style={s.calloutBody}>{children}</div>
    </div>
  );
}

function calcTotal(data, keys) {
  return keys.reduce((sum, k) => sum + (parseInt(data[k]) || 0), 0);
}

export default function WorkbookDay2({ user, onNav }) {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showPostUnlockModal, setShowPostUnlockModal] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const load = async () => {
      const wbSnap = await getDoc(doc(db, 'workbooks', user.uid));
      if (wbSnap.exists()) {
        const prog = wbSnap.data().progress || {};
        const d = prog['day2'] || {};
        setData(d.sections || {});
        setIsComplete(d.status === 'complete');
      }
    };
    load();
  }, [user.uid]);

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day2: { ...(existing.progress?.day2 || {}), sections: data, updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setSaving(false);
    setLastSaved(new Date());
  };

  const handleMarkComplete = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day2: { ...(existing.progress?.day2 || {}), sections: data, status: 'complete', completedAt: serverTimestamp(), updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setIsComplete(true);
    setShowCompleteModal(false);
    setSaving(false);
    // Check if all assigned workbooks are now complete, unlock post-assessment if so
    const unlockResult = await checkAndUnlockPostAssessment(user.uid);
    if (unlockResult.unlocked) {
      setShowPostUnlockModal(true);
    }
  };

  const handleUnmarkComplete = async () => {
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      progress: { ...(existing.progress || {}), day2: { ...(existing.progress?.day2 || {}), status: 'incomplete', updatedAt: serverTimestamp() } }
    }, { merge: true });
    setIsComplete(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(AI_SCORING_PROMPT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const chainTotal = calcTotal(data, ['chain_builds', 'chain_brevity', 'chain_value', 'chain_overall']);
  const iterTotal = calcTotal(data, ['iter_move', 'iter_fix', 'iter_improve', 'iter_overall']);

  return (
    <div style={s.wrapper}>
      <div style={s.topBar}>
        <span style={s.backLink} onClick={() => onNav('workbooks')}>← Back to Workbooks</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isComplete && <div style={s.completeBadge}>✓ Day 2 Complete</div>}
          {lastSaved && <span style={s.savedNote}>Saved {lastSaved.toLocaleTimeString()}</span>}
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Progress'}
          </button>
        </div>
      </div>

      <WorkbookVideoPlayer dayNumber={2} user={user} />

      <div style={s.header}>
        <div style={s.dayTag}>Day 2</div>
        <div style={s.headerTitle}>Advanced Prompting</div>
        <div style={s.headerDesc}>Chain it. Iterate it. Stop starting over.</div>
      </div>

      <div style={s.keyConceptBox}>
        <div style={s.keyConceptLabel}>Key Concept Reminder</div>
        <div style={s.keyConceptText}>AI doesn't forget the conversation. Every response lives in the thread, which means every follow-up prompt can build on what came before without re-explaining the background. Chaining means using the output of one prompt as the input for the next. Your chain prompt says "now do something specific with that." Iterating means fixing the one thing that's off without starting over. Three moves: Redirect (wrong tone or framing), Sharpen (too long, too vague, or too generic), Add Constraint (included something it shouldn't have). One move per iteration: change one thing, run it again, see what changed. You're not writing prompts anymore. You're having a conversation.</div>
      </div>

      <Section title="In-Session Exercise — Chain It and Iterate It">
        <div style={s.exerciseIntro}>Use the RICE prompts you already have from Day 1 and your between-session assignment. Not new tasks: the real prompts you already built and ran.</div>

        <div style={s.partHeader}>PART 1 — CHAIN IT</div>
        <div style={s.partIntro}>Pick one of your Day 1 prompts that produced a useful output. Ask yourself: if this first prompt gets me X, what's the most useful thing I could do with X next? Your chain prompt should be shorter than your original RICE prompt. The context is already in the thread.</div>

        <Field label="Which Day 1 prompt are you chaining from? (Describe the task):" value={data.chain_from} onChange={v => set('chain_from', v)} />
        <Field label="What did the first prompt produce? (Brief summary):" value={data.chain_produced} onChange={v => set('chain_produced', v)} />
        <Field label="Your Chain Prompt:" value={data.chain_prompt} onChange={v => set('chain_prompt', v)} minHeight={80} />
        <Field label="What came back from the chain prompt:" value={data.chain_result} onChange={v => set('chain_result', v)} minHeight={70} />

        <div style={s.field}>
          <div style={s.fieldLabel}>Was it shorter than your original RICE prompt?</div>
          <div style={{ display: 'flex', gap: '20px', margin: '8px 0' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
              <input type="radio" name="shorter" checked={data.chain_shorter === 'yes'} onChange={() => set('chain_shorter', 'yes')} /> Yes — good
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
              <input type="radio" name="shorter" checked={data.chain_shorter === 'no'} onChange={() => set('chain_shorter', 'no')} /> No — what context did you re-explain that was already there?
            </label>
          </div>
          {data.chain_shorter === 'no' && (
            <textarea style={{ ...s.textarea, minHeight: 55 }} value={data.chain_shorter_explain || ''} onChange={e => set('chain_shorter_explain', e.target.value)} />
          )}
        </div>

        <div style={s.partHeader}>PART 2 — ITERATE IT</div>
        <div style={s.partIntro}>Think of a response from Day 1 that was close but not quite right. Identify which of the three moves fits, then write the iteration prompt: one sentence, one specific fix.</div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px', marginBottom: '20px' }}>
          {[
            { key: 'redirect', label: 'REDIRECT', desc: 'Wrong tone, angle, or framing' },
            { key: 'sharpen', label: 'SHARPEN', desc: 'Too long, vague, or generic' },
            { key: 'constraint', label: 'ADD CONSTRAINT', desc: "Included something it shouldn't have" },
          ].map(m => (
            <label key={m.key} style={{ ...s.moveCard, cursor: 'pointer', background: data.iter_move_type === m.key ? '#F2F0EC' : '#fff', borderColor: data.iter_move_type === m.key ? '#1B2A4A' : '#E8E6E2' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <input type="checkbox" checked={data.iter_move_type === m.key} onChange={() => set('iter_move_type', data.iter_move_type === m.key ? '' : m.key)} />
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#1B2A4A' }}>{m.label}</div>
              </div>
              <div style={{ fontSize: '11px', color: '#3D3D3D', fontWeight: '300' }}>{m.desc}</div>
            </label>
          ))}
        </div>

        <Field label="Which prompt are you iterating? (Describe the task):" value={data.iter_from} onChange={v => set('iter_from', v)} />
        <Field label="What specifically was off about the response?" value={data.iter_off} onChange={v => set('iter_off', v)} />
        <Field label="Your Iteration Prompt (one sentence, one fix):" value={data.iter_prompt} onChange={v => set('iter_prompt', v)} minHeight={60} />
        <Field label="What changed in the output after the iteration?" value={data.iter_result} onChange={v => set('iter_result', v)} minHeight={60} />
      </Section>

      <Section title="Scoring Criteria">
        <div style={{ fontSize: '13px', color: '#3D3D3D', marginBottom: '16px', lineHeight: '1.6' }}>Use these criteria to evaluate the quality of your chain prompt and your iteration prompt.</div>

        <div style={{ fontSize: '13px', fontWeight: '700', color: '#1B2A4A', marginBottom: '10px' }}>CHAIN PROMPT SCORING</div>
        <RubricTable rows={CHAIN_RUBRIC} />

        <div style={{ fontSize: '13px', fontWeight: '500', color: '#1B2A4A', margin: '16px 0 12px' }}>Score your chain prompt:</div>
        <div style={s.scoreRow}>
          <ScoreField label="Builds" value={data.chain_builds} onChange={v => set('chain_builds', v)} />
          <ScoreField label="Brevity" value={data.chain_brevity} onChange={v => set('chain_brevity', v)} />
          <ScoreField label="Value" value={data.chain_value} onChange={v => set('chain_value', v)} />
          <ScoreField label="Overall" value={data.chain_overall} onChange={v => set('chain_overall', v)} />
          <div style={s.scoreTotalBox}><div style={s.scoreTotalLabel}>Total</div><div style={s.scoreTotalVal}>{chainTotal} / 16</div></div>
        </div>

        <div style={{ fontSize: '13px', fontWeight: '700', color: '#1B2A4A', margin: '20px 0 10px' }}>ITERATION PROMPT SCORING</div>
        <RubricTable rows={ITERATION_RUBRIC} />

        <div style={{ fontSize: '13px', fontWeight: '500', color: '#1B2A4A', margin: '16px 0 12px' }}>Score your iteration prompt:</div>
        <div style={s.scoreRow}>
          <ScoreField label="Move" value={data.iter_move} onChange={v => set('iter_move', v)} />
          <ScoreField label="Fix" value={data.iter_fix} onChange={v => set('iter_fix', v)} />
          <ScoreField label="Improve" value={data.iter_improve} onChange={v => set('iter_improve', v)} />
          <ScoreField label="Overall" value={data.iter_overall} onChange={v => set('iter_overall', v)} />
          <div style={s.scoreTotalBox}><div style={s.scoreTotalLabel}>Total</div><div style={s.scoreTotalVal}>{iterTotal} / 16</div></div>
        </div>
      </Section>

      <div style={s.aiBox}>
        <div style={s.aiBoxTitle}>AI Scoring Prompt — Evaluate Your Own Work</div>
        <div style={s.aiBoxSub}>Run this twice: once for your chain prompt, once for your iteration prompt. Paste each one separately along with the relevant context.</div>
        <div style={s.aiBoxText}>{AI_SCORING_PROMPT}</div>
        <button style={s.copyBtn} onClick={handleCopy}>{copied ? 'Copied!' : 'Copy AI Scoring Prompt'}</button>
      </div>

      <Section title="Scores from AI Evaluation">
        <Field label="Chain Prompt — AI Score: ___ / 16     Most important improvement:" value={data.ai_chain_score} onChange={v => set('ai_chain_score', v)} />
        <Field label="Iteration Prompt — AI Score: ___ / 16     Most important improvement:" value={data.ai_iter_score} onChange={v => set('ai_iter_score', v)} />
        <Field label="Did the AI's rewritten versions change anything significant? What did you learn?" value={data.ai_learn} onChange={v => set('ai_learn', v)} minHeight={80} />
      </Section>

      <CalloutBox title="THE STANDARD: 16 / 16">
        Keep working until you have a chain prompt AND an iteration prompt that each score 16/16. Use the revision workspace below. Run the AI scoring prompt again with each revised version. Keep going until both hit the standard.
      </CalloutBox>

      <Section title="Revision Workspace — Keep Going Until Both Hit 16/16">
        <div style={s.revisionLabel}>Chain Prompt Revisions</div>
        <Field label="CHAIN REVISION 1 — What I changed and why:" value={data.chain_rev1_why} onChange={v => set('chain_rev1_why', v)} />
        <Field label="Revised version:" value={data.chain_rev1_text} onChange={v => set('chain_rev1_text', v)} minHeight={70} />
        <Field label="New AI Score: ___ / 16" value={data.chain_rev1_score} onChange={v => set('chain_rev1_score', v)} minHeight={40} />
        <Field label="CHAIN REVISION 2 (if needed) — What I changed and why:" value={data.chain_rev2_why} onChange={v => set('chain_rev2_why', v)} />
        <Field label="Revised version:" value={data.chain_rev2_text} onChange={v => set('chain_rev2_text', v)} minHeight={70} />
        <Field label="New AI Score: ___ / 16" value={data.chain_rev2_score} onChange={v => set('chain_rev2_score', v)} minHeight={40} />

        <div style={{ ...s.revisionLabel, marginTop: '24px' }}>Iteration Prompt Revisions</div>
        <Field label="ITERATION REVISION 1 — What I changed and why:" value={data.iter_rev1_why} onChange={v => set('iter_rev1_why', v)} />
        <Field label="Revised version:" value={data.iter_rev1_text} onChange={v => set('iter_rev1_text', v)} minHeight={70} />
        <Field label="New AI Score: ___ / 16" value={data.iter_rev1_score} onChange={v => set('iter_rev1_score', v)} minHeight={40} />
        <Field label="ITERATION REVISION 2 (if needed) — What I changed and why:" value={data.iter_rev2_why} onChange={v => set('iter_rev2_why', v)} />
        <Field label="Revised version:" value={data.iter_rev2_text} onChange={v => set('iter_rev2_text', v)} minHeight={70} />
        <Field label="New AI Score: ___ / 16" value={data.iter_rev2_score} onChange={v => set('iter_rev2_score', v)} minHeight={40} />
      </Section>

      <Section title="Reflection">
        <Field label="1. Think about the chain prompt you built. What does the second step produce that you could never get from a single RICE prompt? Why does that matter for your actual work?" value={data.reflection_1} onChange={v => set('reflection_1', v)} minHeight={80} />
        <Field label="2. When you used the iteration move, which one did you choose: Redirect, Sharpen, or Add Constraint? What told you that was the right one rather than the other two?" value={data.reflection_2} onChange={v => set('reflection_2', v)} minHeight={80} />
      </Section>

      <Section title="Wins Tracker — Day 2">
        <div style={s.winsTable}>
          <div style={s.winsHeader}>
            {['What I Tried', 'Tool Used', 'What Changed', 'Time Saved'].map(h => (
              <div key={h} style={s.winsCol}>{h}</div>
            ))}
          </div>
          {[1, 2, 3].map(n => (
            <div key={n} style={s.winsRow}>
              {['tried', 'tool', 'changed', 'saved'].map(f => (
                <div key={f} style={s.winsCell}>
                  <textarea style={s.winsInput} value={data[`wins_${n}_${f}`] || ''} onChange={e => set(`wins_${n}_${f}`, e.target.value)} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </Section>

      <CalloutBox title="BEFORE DAY 3 — YOUR ASSIGNMENT">
        <span>Build 2 more RICE prompts from different tasks in your actual work: different roles, different workflows. For each one:</span>
        <ol style={{ paddingLeft: '18px', marginTop: '8px', lineHeight: '1.8' }}>
          <li>Write the full RICE prompt and run it.</li>
          <li>Write one chain prompt that builds forward from the output.</li>
          <li>Run the AI scoring prompt on both. Note the scores.</li>
          <li>Revise until each hits 16/16.</li>
          <li>Log all four entries in your Prompt Library with all six fields complete.</li>
          <li>Bring everything to Day 3. We're using it.</li>
        </ol>
      </CalloutBox>
      <Field label="Write your two tasks here so you don't forget them:" value={data.assignment_tasks} onChange={v => set('assignment_tasks', v)} minHeight={70} />

      <Section title="My Prompt Library">
        <div style={s.libraryIntro}>Your Prompt Library is a working tool, not a folder of things you save and forget. Every significant prompt you build goes here with all six fields complete. The output note and chain prompt fields are the ones that make this useful to anyone, including future you.</div>
        {[1, 2, 3].map(n => (
          <div key={n} style={s.libraryEntry}>
            <div style={s.libraryEntryTitle}>Entry {n}</div>
            {[
              { key: 'usecase', label: '01 USE CASE', hint: 'What specific task does this prompt handle?' },
              { key: 'workflow', label: '02 WORKFLOW', hint: 'Which larger workflow does this step belong to?' },
              { key: 'roletag', label: '03 ROLE TAG', hint: 'Your role with enough context to shape the output.' },
              { key: 'prompt', label: '04 THE PROMPT', hint: 'Full prompt text. Every bracket clearly marked.' },
              { key: 'output', label: '05 OUTPUT NOTE', hint: 'One sentence: what does AI produce and where does it go?' },
              { key: 'chain', label: '06 CHAIN PROMPT', hint: 'The next logical step you would take with that output.' },
            ].map(field => (
              <div key={field.key} style={s.libraryRow}>
                <div style={s.libraryFieldLabel}>
                  <div style={s.libraryFieldName}>{field.label}</div>
                  <div style={s.libraryFieldHint}>{field.hint}</div>
                </div>
                <textarea
                  style={{ ...s.textarea, minHeight: field.key === 'prompt' ? 80 : 50, flex: 1, border: 'none', borderRadius: 0 }}
                  value={data[`lib_${n}_${field.key}`] || ''}
                  onChange={e => set(`lib_${n}_${field.key}`, e.target.value)}
                />
              </div>
            ))}
          </div>
        ))}
      </Section>

      <div style={s.bottomBar}>
        <div style={s.saveRow}>
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Progress'}</button>
          {lastSaved && <span style={s.savedNote}>Last saved {lastSaved.toLocaleTimeString()}</span>}
        </div>
        {isComplete ? (
          <div style={s.completeRow}>
            <div style={s.completedBadge}>✓ Day 2 Complete</div>
            <button style={s.unmarkBtn} onClick={handleUnmarkComplete}>Unmark as Complete</button>
          </div>
        ) : (
          <button style={s.completeBtn} onClick={() => setShowCompleteModal(true)}>Mark Day 2 as Complete</button>
        )}
      </div>

      {showCompleteModal && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <div style={s.modalTitle}>Mark Day 2 as Complete?</div>
            <div style={s.modalText}>This will count toward your certificate progress. You can unmark it if you need to come back.</div>
            <div style={s.modalBtns}>
              <button style={s.modalCancel} onClick={() => setShowCompleteModal(false)}>Cancel</button>
              <button style={s.modalConfirm} onClick={handleMarkComplete}>Yes, Mark Complete</button>
            </div>
          </div>
        </div>
      )}

      <WorkbookCompletionModal
        isOpen={showPostUnlockModal}
        onClose={() => setShowPostUnlockModal(false)}
        onGoToAssessment={() => { setShowPostUnlockModal(false); onNav('assessment'); }}
        onGoToExam={() => { setShowPostUnlockModal(false); onNav('exam'); }}
      />
    </div>
  );
}

const s = {
  wrapper: { maxWidth: '720px', paddingBottom: '60px' },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '12px 20px', marginBottom: '16px', position: 'sticky', top: '52px', zIndex: 50 },
  backLink: { fontSize: '12px', color: '#1B2A4A', cursor: 'pointer', fontWeight: '500' },
  header: { background: '#1B2A4A', padding: '24px 28px', marginBottom: '20px' },
  dayTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '6px' },
  headerTitle: { fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#fff', marginBottom: '6px' },
  headerDesc: { fontSize: '13px', color: 'rgba(255,255,255,0.8)', fontWeight: '300' },
  completeBadge: { display: 'inline-block', background: '#1a8a4a', color: '#fff', fontSize: '11px', fontWeight: '700', padding: '4px 12px' },
  keyConceptBox: { background: '#F2F0EC', borderLeft: '3px solid #D4A847', padding: '16px 20px', marginBottom: '20px' },
  keyConceptLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  keyConceptText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.8' },
  section: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '24px', marginBottom: '16px' },
  sectionTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', marginBottom: '16px', paddingBottom: '10px', borderBottom: '1px solid #E8E6E2' },
  exerciseIntro: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '20px' },
  partHeader: { fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#1B2A4A', background: '#F2F0EC', padding: '10px 14px', marginBottom: '12px', borderLeft: '3px solid #D4A847' },
  partIntro: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '16px' },
  field: { marginBottom: '16px' },
  fieldLabel: { fontSize: '13px', fontWeight: '500', color: '#1B2A4A', marginBottom: '6px', lineHeight: '1.5' },
  textarea: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', lineHeight: '1.6' },
  moveCard: { border: '1.5px solid #E8E6E2', padding: '14px', display: 'block' },
  rubricTable: { border: '1.5px solid #E8E6E2', fontSize: '12px', marginBottom: '8px' },
  rubricHeader: { display: 'grid', gridTemplateColumns: '140px 1fr 1fr 1fr 1fr', background: '#1B2A4A', color: '#FFFFFF', fontWeight: '700', fontSize: '11px' },
  rubricRow: { display: 'grid', gridTemplateColumns: '140px 1fr 1fr 1fr 1fr', borderBottom: '1px solid #E8E6E2' },
  rubricCol0: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', fontSize: '11px' },
  rubricCol: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', color: '#3D3D3D', lineHeight: '1.5', fontSize: '11px' },
  scoreRow: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' },
  scoreField: { display: 'flex', alignItems: 'center', gap: '6px' },
  scoreLabel: { fontSize: '13px', fontWeight: '700', color: '#1B2A4A' },
  scoreSelect: { border: '1.5px solid #E8E6E2', padding: '6px 8px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', background: '#FAF8F5', outline: 'none', width: '56px' },
  scoreOf: { fontSize: '12px', color: '#3D3D3D' },
  scoreTotalBox: { marginLeft: 'auto', background: '#1B2A4A', padding: '8px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  scoreTotalLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: '2px' },
  scoreTotalVal: { fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '900', color: '#D4A847' },
  aiBox: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  aiBoxTitle: { fontSize: '13px', fontWeight: '600', color: '#D4A847', marginBottom: '6px' },
  aiBoxSub: { fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: '300', marginBottom: '12px' },
  aiBoxText: { fontSize: '12px', color: '#FFFFFF', lineHeight: '1.8', marginBottom: '12px', fontWeight: '300', whiteSpace: 'pre-line' },
  copyBtn: { background: '#D4A847', color: '#1B2A4A', border: 'none', padding: '8px 18px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  revisionLabel: { fontSize: '12px', fontWeight: '700', color: '#1B2A4A', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '12px' },
  callout: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  calloutTitle: { fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  calloutBody: { fontSize: '13px', color: '#FFFFFF', fontWeight: '300', lineHeight: '1.7' },
  winsTable: { border: '1.5px solid #E8E6E2' },
  winsHeader: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', background: '#1B2A4A' },
  winsCol: { padding: '10px 12px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#fff', borderRight: '1px solid rgba(255,255,255,0.1)' },
  winsRow: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', borderBottom: '1px solid #E8E6E2' },
  winsCell: { borderRight: '1px solid #E8E6E2', padding: '4px' },
  winsInput: { width: '100%', border: 'none', padding: '8px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', minHeight: '50px' },
  libraryIntro: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '20px' },
  libraryEntry: { border: '1.5px solid #E8E6E2', marginBottom: '16px' },
  libraryEntryTitle: { background: '#1B2A4A', color: '#D4A847', padding: '8px 16px', fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase' },
  libraryRow: { display: 'flex', borderBottom: '1px solid #E8E6E2' },
  libraryFieldLabel: { width: '140px', flexShrink: 0, background: '#F2F0EC', padding: '12px', borderRight: '1px solid #E8E6E2' },
  libraryFieldName: { fontSize: '11px', fontWeight: '700', color: '#1B2A4A', marginBottom: '4px' },
  libraryFieldHint: { fontSize: '11px', color: '#3D3D3D', fontStyle: 'italic', lineHeight: '1.5' },
  bottomBar: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px', marginBottom: '16px' },
  saveRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  saveBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  savedNote: { fontSize: '11px', color: '#1a8a4a', fontWeight: '500' },
  completeRow: { display: 'flex', alignItems: 'center', gap: '16px' },
  completedBadge: { background: '#eaf3de', color: '#3b6d11', padding: '8px 16px', fontSize: '12px', fontWeight: '700' },
  unmarkBtn: { fontSize: '12px', color: '#3D3D3D', background: 'none', border: '1px solid #E8E6E2', padding: '6px 14px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  completeBtn: { background: '#C0392B', color: '#fff', border: 'none', padding: '12px 28px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(27,42,74,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 },
  modal: { background: '#FAF8F5', padding: '36px 40px', maxWidth: '420px', width: '90%' },
  modalTitle: { fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1B2A4A', marginBottom: '12px' },
  modalText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '24px' },
  modalBtns: { display: 'flex', gap: '12px' },
  modalCancel: { background: 'transparent', color: '#3D3D3D', border: '1.5px solid #E8E6E2', padding: '10px 20px', fontSize: '13px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  modalConfirm: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
};
